import { ReactNode, useRef } from 'react';
import { Box, Dialog, useTheme } from '@mui/material';
import {
  StyledChartWrapper,
  boxIcon,
  iconsWrapper,
  StyledBoxPanIcon,
  StyledChartHeader,
} from './style';
import { MenuPopoverOptions } from 'src/features/trends/components/menu-popover-options';
import { GraphSeriesType } from 'src/features/trends/types/readings';
import { AlarmData } from 'src/features/alarms';
import { exportAsPNG } from 'src/utilities';
import { TimeOptionType } from 'src/features/trends/types';
import { TimeframePopover } from 'src/features/trends/components/timeframe-popover/TimeframePopover';
import { SelectedBoxType } from 'src/features/trends/components/timeframe-bar-options/types';
import { SessionStorageService } from 'src/features/authentication/utils';
import AddChartModalContent from 'src/features/trends/components/add-chart-modal-content/AddChartModalContent';
import { SelectedInputType } from 'src/features/trends/types/selected-input';
import { DateRangeComponent } from '../date-range';
import ArrowForwardIos from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import dayjs from 'dayjs';
import { useToggle } from 'src/hooks/useToggle';
import { useHtmlElement } from 'src/hooks/useHtmlElement';
import { useResponsive } from 'src/hooks/useResponsive';
import { GetAllResponse } from 'src/services/sites/types';
import { DisabledPanIcon } from 'src/assets/icons/DisabledPanIcon';
import { HamburgerIcon } from 'src/assets/icons/HamburgerIcon';
import { HomeIcon } from 'src/assets/icons/HomeIcon';
import { PanIcon } from 'src/assets/icons/PanIcon';
import { ZoomInIcon } from 'src/assets/icons/ZoomInIcon';
import { ZoomOutIcon } from 'src/assets/icons/ZoomOutIcon';
import { EditIcon } from 'src/assets/icons/EditIcon';
import { CloseIcon } from 'src/assets/icons/CloseIcon';

type Props = {
  children: ReactNode;
  zoomOutHandler: () => void;
  zoomInHandler: () => void;
  onResetZoomHandler: () => void;
  onResetTimestamp: () => void;
  isPanAndZoomActive: boolean;
  togglePanAndZoom: () => void;
  pointChartData?: GraphSeriesType[];
  alarms?: AlarmData[];
  removeChart?: (chartId: string) => void;
  id: string;
  timestamps: any;
  onChangeTimestapHandler: (values: [Date, Date]) => void;
  onSelectTimestampHandler: (timeOption: TimeOptionType) => void;
  selectTabletTimeOptionHandler: (timeOption: TimeOptionType) => void;
  setTimestamps: (timestamps: any) => void;
  sessionStorageKey: string;
  selectedBoxSessionStorageKey: string;
  sites?: { value: GetAllResponse[]; loaded: boolean };
  onSelectEventHandler?: (selectedInputs: SelectedInputType[], id: string) => void;
  selectedBox: TimeOptionType;
  setSelectedBox: (value: TimeOptionType) => void;
  changeTimeFormatHandler: (fromDate: Date, toDate: Date) => void;
  fixedSiteId?: string;
  selectedInputs?: SelectedInputType[];
  onEditOpen: () => void;
};

export const ChartWrapper = ({
  children,
  zoomInHandler,
  zoomOutHandler,
  onResetZoomHandler,
  onResetTimestamp,
  isPanAndZoomActive,
  togglePanAndZoom,
  alarms,
  pointChartData,
  removeChart,
  id,
  timestamps,
  onChangeTimestapHandler,
  onSelectTimestampHandler,
  selectTabletTimeOptionHandler,
  setTimestamps,
  sessionStorageKey,
  selectedBoxSessionStorageKey,
  sites,
  onSelectEventHandler,
  selectedBox,
  setSelectedBox,
  changeTimeFormatHandler,
  fixedSiteId,
  selectedInputs,
  onEditOpen,
}: Props) => {
  const { htmlElement, addHtmlElement, removeHtmlElement } = useHtmlElement();
  const theme = useTheme();
  const iconColor = theme.palette.grey[500];
  const chartRef = useRef(null);
  const { state, onToggle } = useToggle();

  const onSelectBoxHandler = (value: SelectedBoxType) => {
    setSelectedBox(value);
    SessionStorageService.set(selectedBoxSessionStorageKey, value);
  };

  const onDeferredResetZoom = () => {
    setTimeout(onResetZoomHandler, 0);
  };

  const isTablet = useResponsive('down', 'lg');

  const onRemoveHandler = () => {
    if (removeChart && id) removeChart(id);
    onClosePopover();
  };

  const onOpenPopover = (event: any) => {
    addHtmlElement(event.currentTarget);
  };

  const onClosePopover = () => {
    removeHtmlElement();
  };

  const handleDownloadPNGClick = () => {
    onClosePopover();
    exportAsPNG(chartRef);
  };

  const onSelectDataHandler = () => {
    onToggle();
    onClosePopover();
  };

  const onResetHandler = () => {
    onDeferredResetZoom();
    setTimeout(onResetTimestamp, 0);
  };

  const to = dayjs(timestamps.toDate);
  const forwardDisabled = !to.isBefore(dayjs(), 'minute');

  const shiftTimeframe = (direction: 'backward' | 'forward') => {
    const from = dayjs(timestamps.fromDate);
    const to = dayjs(timestamps.toDate);
    const now = dayjs();

    const fromMs = from.valueOf();
    const toMs = to.valueOf();
    const diffMs = toMs - fromMs;
    if (diffMs <= 0) return;

    if (direction === 'backward') {
      const newFrom = dayjs(fromMs - diffMs);
      const newTo = dayjs(toMs - diffMs);

      SessionStorageService.set(
        sessionStorageKey,
        JSON.stringify({ fromDate: newFrom.toISOString(), toDate: newTo.toISOString() })
      );
      setTimeout(() => setTimestamps({ fromDate: newFrom, toDate: newTo }), 0);
      return;
    }

    const candidateTo = dayjs(toMs + diffMs);
    let newTo = candidateTo;
    let newFrom = dayjs(fromMs + diffMs);

    if (candidateTo.isAfter(now)) {
      newTo = now;
      newFrom = now.subtract(diffMs, 'millisecond');
    }

    SessionStorageService.set(
      sessionStorageKey,
      JSON.stringify({ fromDate: newFrom.toISOString(), toDate: newTo.toISOString() })
    );
    setTimeout(() => setTimestamps({ fromDate: newFrom, toDate: newTo }), 0);
  };

  return (
    <StyledChartWrapper theme={theme} ref={chartRef} isTablet={isTablet}>
      <Dialog
        open={state}
        onClose={onToggle}
        sx={{
          '& .MuiPaper-root': {
            width: '950px',
            overflowX: 'hidden',
            maxWidth: '100%',
            boxSizing: 'border-box',
          },
        }}
      >
        <AddChartModalContent
          onSelectEventHandler={onSelectEventHandler}
          onToggleHandler={onToggle}
          isParticularChart
          chartId={id}
          sites={sites}
          fixedSiteId={fixedSiteId}
          selectedInputs={selectedInputs}
        />
      </Dialog>
      <StyledChartHeader isTablet={isTablet}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            minWidth: 0,
            '@media (max-width:600px)': { gap: '2px' },
          }}
        >
          <Box sx={{ cursor: 'pointer', display: 'flex', flexShrink: 0 }} onClick={onEditOpen}>
            <EditIcon fill={iconColor} />
          </Box>

          {isTablet ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={boxIcon} onClick={() => shiftTimeframe('backward')}>
                <ArrowBackIosNew fontSize="small" />
              </Box>

              <TimeframePopover
                timestamps={timestamps}
                onSelectTimestampHandler={onSelectTimestampHandler}
                selectTabletTimeOptionHandler={selectTabletTimeOptionHandler}
                setTimestamps={setTimestamps}
                sessionStorageKey={sessionStorageKey}
                onResetZoomHandler={onDeferredResetZoom}
                selectedBox={selectedBox}
              />

              <Box
                sx={{
                  ...boxIcon,
                  opacity: forwardDisabled ? 0.3 : 1,
                  pointerEvents: forwardDisabled ? 'none' : 'auto',
                }}
                onClick={() => shiftTimeframe('forward')}
              >
                <ArrowForwardIos fontSize="small" />
              </Box>
            </Box>
          ) : null}

          {!isTablet ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={boxIcon} onClick={() => shiftTimeframe('backward')}>
                  <ArrowBackIosNew fontSize="small" />
                </Box>

                <DateRangeComponent
                  onSelectBoxHandler={onSelectBoxHandler}
                  onChangeTimestapHandler={onChangeTimestapHandler}
                  timestamps={timestamps}
                  changeTimeFormatHandler={changeTimeFormatHandler}
                  selectedBox={selectedBox}
                />

                <Box
                  sx={{
                    ...boxIcon,
                    opacity: forwardDisabled ? 0.3 : 1,
                    pointerEvents: forwardDisabled ? 'none' : 'auto',
                  }}
                  onClick={() => shiftTimeframe('forward')}
                >
                  <ArrowForwardIos fontSize="small" />
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>

        <Box sx={iconsWrapper}>
          <Box sx={boxIcon} onClick={zoomInHandler}>
            <ZoomInIcon />
          </Box>
          <Box sx={boxIcon} onClick={zoomOutHandler}>
            <ZoomOutIcon />
          </Box>
          <Box sx={boxIcon} onClick={onResetHandler}>
            <HomeIcon fill={iconColor} />
          </Box>
          <StyledBoxPanIcon
            onClick={togglePanAndZoom}
            isPanAndZoomActive={isPanAndZoomActive}
            theme={theme}
          >
            {isPanAndZoomActive ? <PanIcon /> : <DisabledPanIcon />}
          </StyledBoxPanIcon>
          <Box onClick={onOpenPopover} data-sm="chart-burger-menu" sx={boxIcon}>
            <HamburgerIcon fill={iconColor} />
          </Box>
          <MenuPopoverOptions
            htmlElement={htmlElement}
            onClosePopover={onClosePopover}
            pointChartData={pointChartData}
            alarms={alarms}
            handleDownloadPNGClick={handleDownloadPNGClick}
            onSelectDataHandler={onSelectDataHandler}
          />
          <Box onClick={onRemoveHandler} data-sm="remove-button" sx={boxIcon}>
            <CloseIcon width={14} height={14} fill={iconColor} />
          </Box>
        </Box>
      </StyledChartHeader>
      {children}
    </StyledChartWrapper>
  );
};
